import Axios, { AxiosInstance } from "axios";
import { AnsweringClient } from "../../../../clients/answeringClient";
import { environment } from "../../../../utils/environment";
import { State } from "../../../data-types";

export interface UserAddress {
  id: number;
  label?: string | null;
  postalCode: string;
  street: string;
  number: string;
  completion?: string | null;
  neighborhood: string;
  city: string;
  state: State;
  isDefault: boolean;
  //updatedAt: string;
}

export interface UserAddressPayload {
  label?: string | null;
  postalCode: string;
  street: string;
  number: string;
  completion?: string | null;
  neighborhood: string;
  city: string;
  state: State;
  makeDefault?: boolean;
}

export type UserAddressPatchPayload = Partial<Omit<UserAddressPayload, "makeDefault">> & {
  makeDefault?: boolean;
};

export interface UserInfoFormControllerDelegate {
  showLoading(): void;
  hideLoading(): void;

  didFetchAddresses(addresses: UserAddress[]): void;
  didCreateAddress(addresses: UserAddress[]): void;
  didUpdateAddress(addresses: UserAddress[]): void;
  didDeleteAddress(addresses: UserAddress[]): void;
  didSetDefaultAddress(addresses: UserAddress[]): void;

  applicationShouldLogout(): void;
  genericError(message?: string): void;
}

export class UserInfoFormController {
  private readonly basePath = "api/v1/admin/me/addresses";

  constructor(
    private delegate: UserInfoFormControllerDelegate,
    private readonly http: AxiosInstance = Axios,
    private readonly answeringClient = new AnsweringClient()
  ) { }

  private async handleError(
    error: any,
    retryCallback?: () => Promise<void>
  ): Promise<void> {
    if (error?.response?.status === 403) {
      const newTokens = await this.refreshTokens();
      if (newTokens && retryCallback) {
        await retryCallback();
      }
      return;
    }

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Ocorreu um erro inesperado. Tente novamente.";
    this.delegate.genericError(message);
  }

  public async listAddresses(accessToken: string): Promise<void> {
    this.delegate.showLoading();
    try {
      const response = await this.http.get<UserAddress[]>(
        `${environment.apiBaseUrl}${this.basePath}`,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );
      this.delegate.didFetchAddresses(response.data);
    } catch (error: any) {
      await this.handleError(error, () => this.listAddresses(accessToken));
    } finally {
      this.delegate.hideLoading();
    }
  }

  public async createAddress(
    accessToken: string,
    payload: UserAddressPayload
  ): Promise<void> {
    this.delegate.showLoading();
    try {
      const response = await this.http.post<UserAddress[]>(
        `${environment.apiBaseUrl}${this.basePath}`,
        payload,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );
      this.delegate.didCreateAddress(response.data);
    } catch (error: any) {
      await this.handleError(error, () => this.createAddress(accessToken, payload));
    } finally {
      this.delegate.hideLoading();
    }
  }

  public async updateAddress(
    accessToken: string,
    id: number,
    patch: UserAddressPatchPayload
  ): Promise<void> {
    this.delegate.showLoading();
    try {
      const response = await this.http.patch<UserAddress[]>(
        `${environment.apiBaseUrl}${this.basePath}/${id}`,
        patch,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );
      this.delegate.didUpdateAddress(response.data);
    } catch (error: any) {
      await this.handleError(error, () =>
        this.updateAddress(accessToken, id, patch)
      );
    } finally {
      this.delegate.hideLoading();
    }
  }

  public async deleteAddress(accessToken: string, id: number): Promise<void> {
    this.delegate.showLoading();
    try {
      const response = await this.http.delete<UserAddress[]>(
        `${environment.apiBaseUrl}${this.basePath}/${id}`,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );
      this.delegate.didDeleteAddress(response.data);
    } catch (error: any) {
      await this.handleError(error, () => this.deleteAddress(accessToken, id));
    } finally {
      this.delegate.hideLoading();
    }
  }

  public async setDefaultAddress(
    accessToken: string,
    id: number
  ): Promise<void> {
    this.delegate.showLoading();
    try {
      const response = await this.http.patch<UserAddress[]>(
        `${environment.apiBaseUrl}${this.basePath}/${id}/default`,
        {},
        { headers: { authorization: `Bearer ${accessToken}` } }
      );
      this.delegate.didSetDefaultAddress(response.data);
    } catch (error: any) {
      await this.handleError(error, () =>
        this.setDefaultAddress(accessToken, id)
      );
    } finally {
      this.delegate.hideLoading();
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }

    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens = response.data as { accessToken: string; refreshToken: string };
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch {
      this.delegate.applicationShouldLogout();
    }
  }
}
